import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api, {
  getAccessToken,
  refreshAccessToken,
} from "../api";
import { Link } from "react-router-dom";
import { buySmartTiming, useSubTick, isNative, syncSubToBackend } from "@/iap";
import { useServerSubscription } from "../useServerSubscription";
import { Purchases } from "@revenuecat/purchases-capacitor";
import { trackPurchase, afTrack } from "../analytics/appsflyer";

/** Open the platform's subscription management page */
const openManageSubscriptions = async () => {
  if (!isNative()) {
    try {
      try {
        await refreshAccessToken();
      } catch {}

      const { data } = await api.get(
        "/api/subscriptions/revenuecat/management-url"
      );

      const url =
        data?.management_url;

      if (!url) {
        throw new Error(
          "No subscription management link was returned."
        );
      }

      window.location.assign(url);
    } catch (e) {
      console.error(
        "[Billing] could not open subscription manager",
        e
      );

      alert(
        "We couldn't open subscription management. Please try again."
      );
    }

    return;
  }

  const ua =
    (navigator?.userAgent || "")
      .toLowerCase();

  if (ua.includes("android")) {
    window.open(
      "https://play.google.com/store/account/subscriptions",
      "_blank"
    );
  } else {
    window.open(
      "https://apps.apple.com/account/subscriptions",
      "_blank"
    );
  }
};

async function ensureFreshToken() {
  try {
    return await refreshAccessToken();
  } catch (e) {
    console.warn(
      "[ensureFreshToken] refresh failed",
      e
    );

    return getAccessToken();
  }
}

const getStoreLabel = () => {
  if (!isNative()) {
    return "our secure web checkout provider";
  }

  const ua =
    (navigator?.userAgent || "").toLowerCase();

  if (ua.includes("android")) {
    return "Google Play";
  }

  if (
    ua.includes("iphone") ||
    ua.includes("ipad") ||
    ua.includes("ios")
  ) {
    return "Apple App Store";
  }

  return "your app store";
};

function fmtExpiry(d) {
  if (!d) return "—";
  const date = d instanceof Date ? d : new Date(d);
  const now = new Date();

  const opts = { month: "short", day: "numeric" };
  if (date.getFullYear() !== now.getFullYear()) opts.year = "numeric";

  // Use user's local timezone; no time-of-day, no TZ suffix
  return new Intl.DateTimeFormat(undefined, opts).format(date);
}

async function handleRestore(setServerSub, API) {
  try {
    // Trigger RC restore
    const info = await Purchases.restorePurchases();
    const ent = info?.customerInfo?.entitlements?.active || {};
    const hasPremium = !!(ent["Smart Timing"] || ent.smart_timing || ent.premium || ent.default);

    // Re-sync from your server to reflect webhook/RTDN outcome
    const resp = await api.get(
      "/me/subscription"
    );
    if (resp?.data) {
      const s = resp.data;
      setServerSub({
        active: !!s.active,
        is_premium: !!s.is_premium,
        sub_expiry: s.sub_expiry || null,
      });
    }
    if (!hasPremium) {
      // Not an error; might be lapsed. Keep UI updated from /me/subscription.
      console.log("[Restore] No active entitlement found after restore.");
    }
    alert("Restore completed.");
  } catch (e) {
    console.error("[Restore] failed", e);
    alert(e?.message || "Restore failed. Please try again.");
  }
}

function ActionBar({ onManage, onRestore }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto sm:justify-end">
      <button
        onClick={onManage}
        className="px-3 py-2 rounded-xl border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-white text-sm whitespace-nowrap"
      >
        Manage
      </button>
      <button
        onClick={onRestore}
        className="px-3 py-2 rounded-xl bg-white text-black hover:bg-zinc-200 text-sm whitespace-nowrap"
      >
        Restore purchases
      </button>
    </div>
  );
}

async function waitForRevenueCatReady(timeoutMs = 20000) {
  const start = Date.now();
  if (window.__RC_READY__ === true) return;

  // one-shot event promise (no TS generics)
  let resolved = false;
  const once = new Promise((resolve) => {
    const handler = () => {
      if (!resolved) {
        resolved = true;
        document.removeEventListener("rc-ready", handler);
        resolve();
      }
    };
    document.addEventListener("rc-ready", handler, { once: true });
  });

  while (window.__RC_READY__ !== true) {
    if (Date.now() - start > timeoutMs) {
      throw new Error("Billing not ready. Please try again in a second.");
    }
    // race event vs short sleep; works whether event fires or we just poll
    await Promise.race([once, new Promise((r) => setTimeout(r, 250))]);
    if (window.__RC_READY__ === true) break;
  }
}

function TrustStrip({ variant = "new-user" }) {
  const store = getStoreLabel();

  const copy =
    variant === "new-user"
      ? {
          title: "Before you start",
          bullets: [
            "Creating a Buy Plan is free — no subscription required.",
            "Bitcoin purchases are executed by Strike using your Strike balance when the plan runs.",
            "SatStacker never holds your BTC or payment info, and access to Strike is revocable anytime.",
          ],
        }
      : variant === "pre-plan"
      ? {
          title: "How charges work",
          bullets: [
            "Bitcoin purchases always run through Strike using your Strike balance.",
            `If you upgrade, the subscription fee is billed by ${store} (SatStacker never sees your card).`,
            "SatStacker never custody-holds BTC and cannot move funds without your approval.",
          ],
        }
      : {
          title: "Billing & custody",
          bullets: [
            "BTC purchases run via Strike. SatStacker never holds your funds.",
            `Smart Timing subscription is billed by ${store}.`,
          ],
        };

  return (
    <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-950/40 p-4">
      <p className="text-sm font-semibold text-white mb-2">{copy.title}</p>
      <ul className="space-y-1 text-sm text-zinc-300">
        {copy.bullets.map((b, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-emerald-400">✓</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const API = import.meta.env.VITE_API_BASE_URL || "https://api.satstacker.app";

export default function Pricing({ hasPlan = true }) {
  const tick = useSubTick();
  const [isBuying, setIsBuying] = useState(false);
  const [showActivated, setShowActivated] = useState(false);

  // Keep a local mirror so the UI updates immediately after a purchase,
  // but initialize/sync it from the server-backed hook.
  const [serverSub, setServerSub] = useState({
    active: false,
    is_premium: false,
    sub_expiry: null,
  });

  const { loading, active, is_premium, sub_expiry } = useServerSubscription();

  // sync local mirror whenever server-backed values change
  useEffect(() => {
    setServerSub({
      active: !!active,
      is_premium: !!is_premium,
      sub_expiry: sub_expiry || null,
    });
  }, [active, is_premium, sub_expiry]);

    // Funnel id: persist across pages so BuyPlans + Pricing tie together
    const funnelIdRef = useRef(
      (() => {
        try {
          const existing = sessionStorage.getItem("ss_funnel_id");
          if (existing) return existing;
          const id = crypto.randomUUID();
          sessionStorage.setItem("ss_funnel_id", id);
          return id;
        } catch {
          return crypto.randomUUID();
        }
      })()
    );
    const funnelId = funnelIdRef.current;

    const planContext = useMemo(() => ({
      funnel_id: funnelId,
      page: "pricing",
      has_plan: !!hasPlan,
      platform: isNative() ? "native" : "web",
      store: getStoreLabel(),
    }), [funnelId, hasPlan]);

    useEffect(() => {
      afTrack("pricing_viewed", planContext);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  // derived flags used by your JSX
  const effectiveSubscribed = serverSub.active;
  const expiryDate = serverSub.sub_expiry ? new Date(serverSub.sub_expiry) : null;
  const pendingCancel =
    !effectiveSubscribed &&
    expiryDate instanceof Date &&
    !isNaN(+expiryDate) &&
    expiryDate > new Date();

  useEffect(() => {
    if (!isBuying) return;

    const beforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);

    let removeBack = null;
    try {
      removeBack = window?.Capacitor?.App?.addListener("backButton", () => {
        // swallow back during activation
      });
    } catch (_) {}

    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      if (removeBack?.remove) removeBack.remove();
    };
  }, [isBuying]);

  async function handleSubscribe() {
    if (isBuying) return;
    afTrack("pricing_subscribe_clicked", {
      ...planContext,
      product: "smart_timing_monthly",
      entry: "smart_timing_card",
    });
    setIsBuying(true);
    try {
      const ua = (navigator?.userAgent || "").toLowerCase();

      // Make sure we have a Bearer for the post-purchase server call
      await refreshAccessToken();

      if (ua.includes("android")) {
        const uid = window.__USER_ID__ ? String(window.__USER_ID__) : null;
        if (!uid)
          throw new Error("Please try again in a moment — your account isn’t fully loaded yet.");

        await waitForRevenueCatReady(10000);

        if (
          !Purchases ||
          typeof Purchases.getOfferings !== "function"
        ) {
          throw new Error(
            "Billing not ready (RevenueCat plugin not available)."
          );
        }

        const currentUserId =
          await Purchases.getAppUserID();

        if (currentUserId !== uid) {
          await Purchases.logIn({
            appUserID: uid,
          });

          window.__RC_LOGGED__ = uid;
        }

        const offerings = await Purchases.getOfferings();
        const current = offerings.current;

        console.log(
          "[RC] offerings:",
          JSON.stringify(
            {
              hasCurrent: !!current,
              packages: (current?.availablePackages || []).map((p) => ({
                packageId: p?.identifier,
                storeProductId: p?.product?.identifier,
                price: p?.product?.priceString,
              })),
            },
            null,
            2
          )
        );

        if (!current?.availablePackages?.length) {
          throw new Error(
            "No subscription packages available. Check the current RevenueCat offering."
          );
        }

        const ANDROID_PRODUCT_ID =
          "smart_timing_monthly:smart-timing";

        const pkg =
          current.availablePackages.find(
            (item) =>
              item?.product?.identifier ===
              ANDROID_PRODUCT_ID
          ) || current.availablePackages[0];

        const result = await Purchases.purchasePackage({
          aPackage: pkg,
        });

        // 5) Entitlement check + webhook poll (your code as-is)
        try {
          await syncSubToBackend();
        } catch (e) {
          console.warn("[Pricing] post-purchase sync failed; will rely on polling", e);
        }
        const info = result?.customerInfo || (await Purchases.getCustomerInfo?.());
        const ent = info?.entitlements?.active || {};
        let hasPremium = !!(
          ent["Smart Timing"] ||
          ent.smart_timing ||
          ent.premium ||
          ent.default
        );

        const startPoll = Date.now();
        while (Date.now() - startPoll < 60000) {
          const subRes = await api
            .get("/me/subscription")
            .catch(() => null);

          if (subRes?.data) {
            const body = subRes.data;
            if (body?.is_premium) {
              hasPremium = true;
              break;
            }
          }
          await new Promise((r) => setTimeout(r, 2500));
        }

        if (!hasPremium) {
          const info2 = await Purchases.getCustomerInfo?.();
          const ent2 = info2?.entitlements?.active || {};
          if (
            !(
              ent2["Smart Timing"] ||
              ent2.smart_timing ||
              ent2.premium ||
              ent2.default
            )
          ) {
            throw new Error(
              "Purchase completed but premium not active yet. Please reopen the app shortly."
            );
          }
        }
        trackPurchase({
          price: Number(pkg?.product?.price ?? 1.99),
          currency: pkg?.product?.currencyCode || "USD",
          plan: pkg?.product?.identifier || "smart_timing_monthly",
        });
      } else {
        try {
          await buySmartTiming();
        } catch (e) {
          const msg  = e?.message || "";
          const code = e?.code ?? e?.errorCode;
          const userCancelled =
            e?.userCancelled === true ||
            /cancel/i.test(msg) ||
            code === "PURCHASE_CANCELLED" ||
            code === "PurchaseCancelledError" ||
            code === 1;

          if (userCancelled) {
            afTrack("pricing_subscribe_cancelled", {
              ...planContext,
              product: "smart_timing_monthly",
            });
            return;
          }

          throw e;
        }
        try {
          await syncSubToBackend();
        } catch (syncError) {
          console.warn(
            "[Pricing] purchase succeeded; immediate backend sync failed, waiting for webhook",
            syncError
          );
        }
      }

      // Poll the server for the flipped premium status (approved() posts it)
      let upgraded = false;

      for (let i = 0; i < 10; i++) {
        await new Promise(
          (r) => setTimeout(r, 800)
        );

        const resp = await api.get(
          "/me/subscription"
        );

        if (resp?.data) {
          const s = resp.data;
          setServerSub({
            active: !!s.active,
            is_premium: !!s.is_premium,
            sub_expiry: s.sub_expiry || null,
          });
          if (s?.is_premium) {
            upgraded = true;

            // ✅ iOS purchase tracking (Android already tracks earlier)
            const ua = (navigator?.userAgent || "").toLowerCase();
            if (!ua.includes("android")) {
              trackPurchase({ price: 1.99, currency: "USD", plan: "smart_timing_monthly" });
            }

            break;
          }
        }
      }

      if (upgraded) {
        afTrack("pricing_subscribe_success", {
          ...planContext,
          product: "smart_timing_monthly",
        });
        setShowActivated(true);
      } else {
        // Optional: still show a soft notice
        alert("Purchase completed. Waiting for server to confirm premium…");
      }
    } catch (e) {
      afTrack("pricing_subscribe_failed", {
        ...planContext,
        product: "smart_timing_monthly",
        reason: e?.message ?? "unknown",
      });
      console.error(e);
      alert(e?.message || "Subscription failed");
    } finally {
      setIsBuying(false);
    }
  }

  return (
    <div className="min-h-screen bg-transparent p-6">
      <div className="max-w-3xl mx-auto bg-zinc-900/80 border border-zinc-800 shadow-lg rounded-2xl p-8">
        <h1 className="text-3xl font-bold mb-2 text-center text-white">Pricing</h1>
        <p className="text-center text-zinc-400 mb-8">Choose the plan that's right for you.</p>

        {!hasPlan && <TrustStrip variant="new-user" />}

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic (No Subscription) */}
          <div className="bg-zinc-950/40 border border-zinc-800 rounded-2xl p-6 flex flex-col justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2 text-white">Basic Plan</h2>
              <p className="text-zinc-400 mb-4">Simple, set-and-forget recurring buys.</p>
              <ul className="list-disc list-inside text-zinc-200 mb-6">
                <li>0.75% app fee per transaction</li>
                <li>No subscription required</li>
                <li>Automatic purchases</li>
              </ul>
            </div>
            <Link
              to="/plans?smartTiming=false"
              onClick={() => afTrack("pricing_basic_get_started_clicked", {
                ...planContext,
                entry: "basic_card",
              })}
            >
              <button className="w-full bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700">
                Get Started
              </button>
            </Link>
          </div>

          {/* Smart Timing (Subscription) */}
          <div className="relative bg-blue-950/30 border-2 border-blue-500/70 rounded-2xl p-6 pt-10 flex flex-col justify-between shadow-[0_0_0_1px_rgba(59,130,246,0.15)] hover:shadow-[0_0_0_1px_rgba(59,130,246,0.35)] transition-shadow">
            <span className="absolute top-3 right-3 px-2 py-0.5 text-[11px] font-semibold rounded-md bg-blue-500 text-black">
              Best value
            </span>

            {/* CONTENT */}
            <div>
              <p className="uppercase tracking-wide text-xs font-semibold text-blue-300 mb-2">
                Upgrade for smarter buys
              </p>

              <h2 className="text-2xl font-bold mb-2 text-white">Smart Timing Plan</h2>

              <p className="text-zinc-200 mb-4">
                Smart Timing watches for better entry opportunities within your recurring buy window while keeping your budget and schedule the same.
                No performance is guaranteed.
              </p>

              <div className="flex items-baseline mb-1">
                <span className="text-4xl sm:text-5xl font-extrabold text-white mr-1">$1.99</span>
                <span className="text-lg text-zinc-200">/month</span>
              </div>
              <p className="text-sm text-blue-300 font-medium mb-4">
                First month free — then $1.99/mo. Cancel anytime.
              </p>

              <div className="mt-2 rounded-lg border border-blue-400/20 bg-black/20 px-3 py-2 text-xs text-blue-100">
                <div className="flex flex-col gap-1">
                  <div>✓ Billed by {getStoreLabel()}</div>
                  <div>✓ SatStacker never sees or stores your card</div>
                </div>
              </div>

              <ul className="list-disc pl-5 text-blue-200 mb-6 space-y-1">
                <li>0.25% app fee per transaction (subscriber rate)</li>
                <li>Shifts more of your scheduled buy into pullbacks</li>
                <li>Adapts buy size to volatility (rules-based)</li>
                <li>Automatic purchases</li>
              </ul>

              <div className="rounded-lg border border-blue-400/30 bg-black/30 p-3 text-sm text-blue-100">
                {/* Mobile: collapsible */}
                <details className="sm:hidden">
                  <summary className="cursor-pointer font-semibold text-white">
                    How Smart Timing works
                  </summary>
                  <div className="mt-2">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Monitors price and momentum within your chosen cadence.</li>
                      <li>Shifts more of your scheduled buy into dips.</li>
                      <li>Keeps everything automated—no charts or micromanagement needed.</li>
                    </ul>
                    <p className="text-xs text-blue-200/80 mt-2">
                      No performance is guaranteed. Outcomes vary with market conditions.
                    </p>
                  </div>
                </details>

                {/* Desktop: always open */}
                <div className="hidden sm:block">
                  <p className="font-semibold mb-1 text-white">How Smart Timing works</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Monitors price and momentum within your chosen cadence.</li>
                    <li>Shifts more of your scheduled buy into dips.</li>
                    <li>Keeps everything automated—no charts or micromanagement needed.</li>
                  </ul>
                  <p className="text-xs text-blue-200/80 mt-2">
                    No performance is guaranteed. Outcomes vary with market conditions.
                  </p>
                </div>
              </div>
            </div>

            {/* ACTIONS / STATUS (kept INSIDE the card) */}
            <div className="mt-4">
              {/* Subscribe button (only when NOT effectively subscribed and not pending-cancel) */}
              {isNative() &&
                !!getAccessToken() &&
                !effectiveSubscribed &&
                !pendingCancel && (
                  <>
                  <button
                    onClick={handleSubscribe}
                    disabled={isBuying}
                    className={`w-full py-2.5 rounded-xl text-white font-semibold ${
                      isBuying ? "bg-blue-400 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-400"
                    }`}
                  >
                    {isBuying ? "Processing…" : "Start free month →"}
                  </button>
                  <p className="text-center text-xs text-zinc-500 mt-2">
                    First month free. $1.99/mo after. Cancel anytime.
                  </p>
                  </>
                )}

                {!isNative() &&
                  !!getAccessToken() &&
                  !effectiveSubscribed &&
                  !pendingCancel && (
                    <>
                      <Link
                        to="/plans?smartTiming=1"
                        onClick={() =>
                          afTrack(
                            "pricing_web_smart_timing_clicked",
                            {
                              ...planContext,
                              entry: "smart_timing_card",
                            }
                          )
                        }
                      >
                        <button
                          type="button"
                          className="w-full py-2.5 rounded-xl text-white font-semibold bg-blue-500 hover:bg-blue-400"
                        >
                          Start free month →
                        </button>
                      </Link>

                      <p className="text-center text-xs text-zinc-500 mt-2">
                        Set up your plan, then start your free month.
                      </p>
                    </>
                  )}

                {!getAccessToken() && !effectiveSubscribed && !pendingCancel && (
                  <Link to="/signup?plan=smart-timing">
                    <button className="w-full py-2.5 rounded-xl text-white font-semibold bg-blue-500 hover:bg-blue-400">
                      Get Started
                    </button>
                  </Link>
                )}

              {/* Restore hint when not subscribed (native only) */}
              {isNative() && !effectiveSubscribed && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => handleRestore(setServerSub, API)}
                    className="text-sm text-zinc-300 underline hover:text-white"
                  >
                    Restore purchases
                  </button>
                </div>
              )}

              {/* Pending-cancel notice */}
              {pendingCancel && (
                <section className="mt-4">
                  <div className="rounded-2xl border border-amber-400/40 bg-amber-950/30 p-4">
                    <div className="flex justify-center sm:justify-start">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-amber-500 text-black text-sm shrink-0">
                        ! Expiring
                      </span>
                      <span className="text-sm text-amber-100 break-words">
                        Cancels on <span className="font-medium">{fmtExpiry(expiryDate)}</span>
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <button
                        onClick={() => {
                          afTrack("pricing_manage_clicked", { ...planContext, entry: "smart_timing_card" });
                          openManageSubscriptions();
                        }}
                        className="w-full px-3 py-2 rounded-xl border border-amber-400/40 bg-zinc-950/40 hover:bg-zinc-900 text-amber-100 text-sm"
                      >
                        Manage
                      </button>
                      <button
                        onClick={() => {
                          afTrack("pricing_restore_clicked", { ...planContext, entry: "smart_timing_card" });
                          handleRestore(setServerSub, API);
                        }}
                        className="w-full px-3 py-2 rounded-xl bg-amber-500 text-black hover:bg-amber-400 text-sm"
                      >
                        Restore purchases
                      </button>
                    </div>
                  </div>
                </section>
              )}

              {/* Subscribed pill + actions */}
              {effectiveSubscribed && !pendingCancel && (
                <section className="mt-4">
                  <div className="rounded-2xl border border-emerald-400/30 bg-emerald-950/25 p-4">
                    <div className="flex justify-center sm:justify-start">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-emerald-500 text-black text-sm shrink-0">
                        ✓ Subscribed
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <button
                        onClick={openManageSubscriptions}
                        className="w-full px-3 py-2 rounded-xl border border-zinc-700 bg-zinc-950/40 hover:bg-zinc-900 text-white text-sm"
                      >
                        Manage
                      </button>
                      <button
                        onClick={() => handleRestore(setServerSub, API)}
                        className="w-full px-3 py-2 rounded-xl bg-white text-black hover:bg-zinc-200 text-sm"
                      >
                        Restore purchases
                      </button>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>

        {/* Fee details / disclosure */}
        <div className="mt-8 rounded-xl border border-zinc-800 bg-zinc-950/40 p-4">
          <h3 className="font-semibold text-white mb-2">Fee details</h3>
          <ul className="list-disc list-inside text-zinc-300 space-y-1">
            <li>
              The <span className="font-medium text-zinc-100">app fee</span> is{" "}
              <span className="font-medium text-zinc-100">0.25%</span> for subscribers and{" "}
              <span className="font-medium text-zinc-100">0.75%</span> for non-subscribers.
            </li>
            <li>
              <span className="font-medium text-zinc-100">Strike processing fees</span> (if any) are
              separate and shown in your transaction details.
            </li>
            <li>
              App fees are taken from your fiat amount <span className="italic">before</span>{" "}
              converting to BTC (we issue a small Strike Lightning invoice, then convert the net USD
              to BTC).
            </li>
          </ul>
        </div>

        <p className="text-xs text-zinc-400 mt-4 text-center">
          Subscriptions are billed by {getStoreLabel()} and auto-renew until canceled.{" "}
          SatStacker does not store your card details. Cancel anytime.{" "}
          By subscribing you agree to our{" "}
          <Link to="/terms" className="underline hover:text-white">
            Terms of Use
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="underline hover:text-white">
            Privacy Policy
          </Link>
          .
        </p>

        {/* Web-only store links (optional) */}
        {!isNative() && (
          <div className="mt-8 text-center">
            <p className="text-sm text-zinc-400">
              Prefer the mobile app? Get Smart Timing on:
              <br />
              <a
                href="https://play.google.com/store/apps/details?id=com.satstacker.app"
                className="text-blue-400 underline hover:text-blue-300"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => afTrack("pricing_store_link_clicked", {
                  ...planContext,
                  store: "google_play",
                })}
              >
                Google Play
              </a>{" "}
              ·{" "}
              <a
                href="https://apps.apple.com/app/id6748700654"
                className="text-blue-400 underline hover:text-blue-300"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => afTrack("pricing_store_link_clicked", {
                  ...planContext,
                  store: "app_store",
                })}
              >
                App Store
              </a>
              .
            </p>
          </div>
        )}
      </div>

      {isBuying && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(2px)",
          }}
          aria-busy="true"
          aria-live="polite"
        >
          <div className="rounded-lg bg-black/80 text-white px-6 py-4 text-center">
            <div className="mb-2 text-lg font-semibold">Activating your subscription…</div>
            <div className="text-sm opacity-80">Please don’t close the app or navigate away.</div>
          </div>
        </div>
      )}

      {showActivated && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="rounded-2xl bg-zinc-900 border border-zinc-800 shadow-xl p-6 max-w-sm w-[90%] text-center text-white">
            <h3 className="text-lg font-semibold mb-2">Subscription Activated</h3>
            <p className="text-zinc-300 mb-4">Your Smart Timing features are now unlocked.</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setShowActivated(false)}
                className="px-4 py-2 rounded-xl bg-white text-black hover:bg-zinc-200"
              >
                Got it
              </button>
              <a
                href="/plans?smartTiming=true"
                className="px-4 py-2 rounded-xl border border-zinc-700 hover:bg-zinc-800"
                onClick={() => {
                  afTrack("pricing_go_to_plans_clicked", { ...planContext, entry: "activated_modal" });
                  setShowActivated(false);
                }}
              >
                Go to Buy Plans
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}